import * as React from 'react';
import { MouseEvent, PointerEvent, useCallback } from 'react';
import { Vector } from '../helpers/Vector';
import type {
    CallIntegrationRequest,
    CallIntegrationResponse,
    ConnectionPortType,
    FunctionPortDefinition,
    IntegrationAccess,
    Node,
    NodeExecutionResult,
    NodeInstance,
    NodeInstanceDataPort,
    NodeInstanceState,
} from '@glueapp/graph-types';
import { FunctionDefinition, FunctionNodeInstance, isFunctionNodeInstance } from '@glueapp/graph-types';
import type { ExecutionError } from '@/GraphEditor/useGraphExecution';
import { NodePositionContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodePositionContext';
import { NodeDimensionsContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeDimensionsContext';
import { NodeShapeContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeShapeContext';
import { NodeRunsContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeRunsContext';
import { NodeEventsContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeEventsContext';
import { PortEventsContextProvider } from './contexts/PortEventsContext';
import { PortPositionsContextProvider } from '@/GraphEditor/drawingLayer/contexts/PortPositionsContext';
import { TriggerRunContextProvider } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { NodeExecutionErrorContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeExecutionErrorContext';
import { NodeSelectedContextProvider } from './contexts/NodeSelectedContext';
import { NodeExecutionResultContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeExecutionResultContext';
import { NodeConnectedPortsContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeConnectedPortsContext';
import { NodeStateContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeStateContext';
import { CallIntegrationContextProvider } from '@/GraphEditor/drawingLayer/contexts/CallIntegrationContext';
import { getNodeComponent } from '@/nodeComponents/NodeComponentsRegistry';
import { ReframeDirection } from '@/GraphEditor/drawingLayer/useNodeResizing';
import { NodeReframeContextProvider } from '@/GraphEditor/drawingLayer/contexts/NodeReframeContext';
import { CurrentNodeContextProvider } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeContext';
import { CurrentNodeInstanceContextProvider } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeInstanceContext';
import { FunctionPortEditingContextProvider } from '@/GraphEditor/drawingLayer/contexts/FunctionPortEditingContext';
import { PortOverlayNodeContextProvider } from '@/GraphEditor/drawingLayer/contexts/PortOverlayNodeContext';
import type { PortOverlaySide, PortOverlayState } from '@/GraphEditor/drawingLayer/portOverlay/portOverlayTypes';

interface Props {
    nodeInstance: NodeInstance;
    node: Node;
    nodeConnectionDictionary: Record<string, Record<string, boolean>>;
    activeOverlay?: PortOverlayState | null;
    openPortOverlay?: (nodeInstanceId: number, portName: string, portSide: PortOverlaySide) => void;
    closePortOverlay?: () => void;

    // Function-related props
    currentFunctionDefinition?: FunctionDefinition;
    allFunctions?: FunctionDefinition[]; // All functions for port computation
    updateFunctionInputs?: (functionId: string, inputPorts: FunctionPortDefinition[]) => void;
    updateFunctionOutputs?: (functionId: string, outputPorts: FunctionPortDefinition[]) => void;

    handleMouseDown: (event: PointerEvent, nodeInstance: NodeInstance) => void;
    handleMouseUp: (event: PointerEvent, nodeInstance: NodeInstance) => void;
    onClick: (event: MouseEvent, nodeInstance: NodeInstance) => void | false;
    onDoubleClick?: (event: MouseEvent, nodeInstance: NodeInstance) => void;

    onOutPortMouseDown: (event: MouseEvent, nodeInstance: NodeInstance, portName: string, portType?: ConnectionPortType) => void;
    onInPortMouseUp: (event: MouseEvent, nodeInstance: NodeInstance, portName: string, portType?: ConnectionPortType) => void;
    onInPortMouseDown: (event: MouseEvent, nodeInstance: NodeInstance, portName: string, portType?: ConnectionPortType) => void;

    setInPortPosition: (nodeInstanceId: number, portName: string, position: Vector) => void;
    setOutPortPosition: (nodeInstanceId: number, portName: string, position: Vector) => void;
    // Intentionally only used to bust the React.memo wrapper below: incrementing this
    // forces a re-render so the ports re-run their measuring useLayoutEffects (e.g. after
    // a zoom change, which is otherwise invisible to this memoized component). Do not
    // remove just because it looks unused — connections will float out of place.
    layoutVersion?: number;

    startNodeReframe: (event: MouseEvent, node: NodeInstance, direction: ReframeDirection) => void;
    endNodeReframe: () => void;

    triggerManualRun: (nodeInstanceId: number) => void;
    runTimes?: Set<number>;

    callIntegration: (integrationAccess: IntegrationAccess, requestConfig: CallIntegrationRequest) => Promise<CallIntegrationResponse<any>>;

    setNodeDimensions: (nodeInstanceId: number, width: number, height: number) => void;
    setNodeState: (nodeInstanceId: number, state: NodeInstanceState) => void;
    setNodeShape: (nodeInstanceId: number, shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[] }) => void;

    executionResult?: NodeExecutionResult;
    executionError?: ExecutionError;
}

const BaseNodeRenderer: React.FC<Props> = ({
    nodeInstance,
    node,
    runTimes,
    currentFunctionDefinition,
    allFunctions,
    updateFunctionInputs,
    updateFunctionOutputs,
    handleMouseDown,
    handleMouseUp,
    onClick,
    onDoubleClick,
    onOutPortMouseDown,
    onInPortMouseUp,
    onInPortMouseDown,
    setInPortPosition,
    setOutPortPosition,
    startNodeReframe,
    endNodeReframe,
    triggerManualRun,
    callIntegration,
    setNodeDimensions,
    setNodeState,
    setNodeShape,
    executionResult,
    executionError,
    nodeConnectionDictionary,
    activeOverlay,
    openPortOverlay,
    closePortOverlay,
}) => {
    const connectedPorts = nodeConnectionDictionary[nodeInstance.id.toString()];

    const nodeSpecificSetNodeDimensions = useCallback(
        (width: number, height: number) => setNodeDimensions(nodeInstance.id, width, height),
        [setNodeDimensions, nodeInstance.id],
    );

    const nodeSpecificSetNodeState = useCallback(
        (state: NodeInstanceState) => setNodeState(nodeInstance.id, state),
        [setNodeState, nodeInstance.id],
    );

    const nodeSpecificSetNodeShape = useCallback(
        (shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[] }) => setNodeShape(nodeInstance.id, shape),
        [setNodeShape, nodeInstance.id],
    );

    const nodeSpecificHandleMouseDown = useCallback(
        (event: PointerEvent) => handleMouseDown(event, nodeInstance),
        [handleMouseDown, nodeInstance],
    );
    const nodeSpecificHandleMouseUp = useCallback(
        (event: PointerEvent) => handleMouseUp(event, nodeInstance),
        [handleMouseUp, nodeInstance],
    );
    const nodeSpecificOnClick = useCallback((event: MouseEvent) => onClick(event, nodeInstance), [onClick, nodeInstance]);
    const nodeSpecificOnDoubleClick = useCallback(
        (event: MouseEvent) => onDoubleClick && onDoubleClick(event, nodeInstance),
        [onDoubleClick, nodeInstance],
    );

    const nodeSpecificOnOutPortMouseDown = useCallback(
        (event: MouseEvent, portName: string, portType?: ConnectionPortType) => onOutPortMouseDown(event, nodeInstance, portName, portType),
        [onOutPortMouseDown, nodeInstance],
    );
    const nodeSpecificOnInPortMouseUp = useCallback(
        (event: MouseEvent, portName: string, portType?: ConnectionPortType) => onInPortMouseUp(event, nodeInstance, portName, portType),
        [onInPortMouseUp, nodeInstance],
    );
    const nodeSpecificOnInPortMouseDown = useCallback(
        (event: MouseEvent, portName: string, portType?: ConnectionPortType) => onInPortMouseDown(event, nodeInstance, portName, portType),
        [onInPortMouseDown, nodeInstance],
    );

    const nodeSpecificSetInPortPosition = useCallback(
        (portName: string, position: Vector) => setInPortPosition(nodeInstance.id, portName, position),
        [setInPortPosition, nodeInstance.id],
    );
    const nodeSpecificSetOutPortPosition = useCallback(
        (portName: string, position: Vector) => setOutPortPosition(nodeInstance.id, portName, position),
        [setOutPortPosition, nodeInstance.id],
    );

    const nodeSpecificStartNodeReframe = useCallback(
        (event: MouseEvent, direction: ReframeDirection) => startNodeReframe(event, nodeInstance, direction),
        [startNodeReframe, nodeInstance],
    );

    const nodeSpecificTriggerRun = useCallback(() => triggerManualRun(nodeInstance.id), [triggerManualRun, nodeInstance.id]);

    const safeOpenPortOverlay = openPortOverlay ?? (() => {});
    const safeClosePortOverlay = closePortOverlay ?? (() => {});
    const resolvedActiveOverlay = activeOverlay ?? null;

    const nodeSpecificOpenOverlay = useCallback(
        (portName: string, portSide: PortOverlaySide) => {
            safeOpenPortOverlay(nodeInstance.id, portName, portSide);
        },
        [safeOpenPortOverlay, nodeInstance.id],
    );

    const nodeSpecificIsOverlayActive = useCallback(
        (portName: string, portSide: PortOverlaySide) => {
            return (
                resolvedActiveOverlay?.nodeInstanceId === nodeInstance.id &&
                resolvedActiveOverlay.portName === portName &&
                resolvedActiveOverlay.portSide === portSide
            );
        },
        [resolvedActiveOverlay, nodeInstance.id],
    );

    const callIntegrationWithSelectedAccess = useCallback(
        async (request: CallIntegrationRequest) => {
            if (nodeInstance.selectedIntegrationAccess) {
                return callIntegration(nodeInstance.selectedIntegrationAccess, request);
            }
            return;
        },
        [nodeInstance.selectedIntegrationAccess, callIntegration],
    );

    const nodeError = executionError?.nodeInstanceId === nodeInstance.id ? executionError.error : undefined;

    const isFunctionInputsNode = nodeInstance.nodeId === 'function-inputs';
    const isFunctionOutputsNode = nodeInstance.nodeId === 'function-outputs';

    let NodeComponent;
    if (isFunctionInputsNode) {
        NodeComponent = getNodeComponent('functionInputs');
    } else if (isFunctionOutputsNode) {
        NodeComponent = getNodeComponent('functionOutputs');
    } else if (isFunctionNodeInstance(nodeInstance)) {
        NodeComponent = getNodeComponent('function');
    } else if (nodeInstance.nodeId === 'Http Request') {
        NodeComponent = getNodeComponent('httpRequest');
    } else if (nodeInstance.nodeId === 'FormTrigger') {
        NodeComponent = getNodeComponent('formTrigger');
    } else if (nodeInstance.nodeId === 'ApiServer') {
        NodeComponent = getNodeComponent('apiServer');
    } else if (nodeInstance.nodeId === 'Unpack') {
        NodeComponent = getNodeComponent('unpack');
    } else if (node.interfaceComponentId) {
        NodeComponent = getNodeComponent('standardWithComponent');
    } else {
        NodeComponent = getNodeComponent('standard');
    }

    const handleUpdateFunctionInputs = useCallback(
        (inputPorts: FunctionPortDefinition[]) => {
            if (updateFunctionInputs && isFunctionInputsNode && currentFunctionDefinition?.id) {
                updateFunctionInputs(currentFunctionDefinition.id, inputPorts);
            }
        },
        [currentFunctionDefinition?.id, updateFunctionInputs, isFunctionInputsNode],
    );

    const handleUpdateFunctionOutputs = useCallback(
        (outputPorts: FunctionPortDefinition[]) => {
            if (updateFunctionOutputs && isFunctionOutputsNode && currentFunctionDefinition?.id) {
                updateFunctionOutputs(currentFunctionDefinition.id, outputPorts);
            }
        },
        [currentFunctionDefinition?.id, updateFunctionOutputs, isFunctionOutputsNode],
    );

    return (
        <CurrentNodeContextProvider node={node}>
            <CurrentNodeInstanceContextProvider nodeInstance={nodeInstance}>
                <NodePositionContextProvider nodePosition={nodeInstance.position}>
                    <NodeDimensionsContextProvider setNodeDimensions={nodeSpecificSetNodeDimensions}>
                        <NodeShapeContextProvider setNodeShape={nodeSpecificSetNodeShape}>
                            <NodeRunsContextProvider runTimes={runTimes}>
                                <NodeEventsContextProvider
                                    onClick={nodeSpecificOnClick}
                                    onDoubleClick={nodeSpecificOnDoubleClick}
                                    handleMouseDown={nodeSpecificHandleMouseDown}
                                    handleMouseUp={nodeSpecificHandleMouseUp}
                                >
                                    <PortEventsContextProvider
                                        onOutPortMouseDown={nodeSpecificOnOutPortMouseDown}
                                        onInPortMouseUp={nodeSpecificOnInPortMouseUp}
                                        onInPortMouseDown={nodeSpecificOnInPortMouseDown}
                                    >
                                        <PortPositionsContextProvider
                                            setInPortPosition={nodeSpecificSetInPortPosition}
                                            setOutPortPosition={nodeSpecificSetOutPortPosition}
                                        >
                                            <TriggerRunContextProvider triggerRun={nodeSpecificTriggerRun}>
                                                <NodeExecutionErrorContextProvider error={nodeError}>
                                                    <NodeSelectedContextProvider nodeSelected={nodeInstance.selected}>
                                                        <NodeExecutionResultContextProvider executionResult={executionResult}>
                                                            <NodeConnectedPortsContextProvider connectedPorts={connectedPorts}>
                                                                <NodeStateContextProvider setNodeState={nodeSpecificSetNodeState}>
                                                                    <CallIntegrationContextProvider
                                                                        setCallIntegration={callIntegrationWithSelectedAccess}
                                                                    >
                                                                        <NodeReframeContextProvider
                                                                            startNodeReframe={nodeSpecificStartNodeReframe}
                                                                            endNodeReframe={endNodeReframe}
                                                                        >
                                                                            <PortOverlayNodeContextProvider
                                                                                openOverlay={nodeSpecificOpenOverlay}
                                                                                closeOverlay={safeClosePortOverlay}
                                                                                isActive={nodeSpecificIsOverlayActive}
                                                                            >
                                                                                {isFunctionInputsNode || isFunctionOutputsNode ? (
                                                                                    <FunctionPortEditingContextProvider
                                                                                        functionDefinition={currentFunctionDefinition}
                                                                                        updateFunctionInputs={handleUpdateFunctionInputs}
                                                                                        updateFunctionOutputs={handleUpdateFunctionOutputs}
                                                                                    >
                                                                                        <NodeComponent
                                                                                            nodeInstance={
                                                                                                nodeInstance as FunctionNodeInstance
                                                                                            }
                                                                                        />
                                                                                    </FunctionPortEditingContextProvider>
                                                                                ) : (
                                                                                    <NodeComponent
                                                                                        nodeInstance={nodeInstance as FunctionNodeInstance}
                                                                                    />
                                                                                )}
                                                                            </PortOverlayNodeContextProvider>
                                                                        </NodeReframeContextProvider>
                                                                    </CallIntegrationContextProvider>
                                                                </NodeStateContextProvider>
                                                            </NodeConnectedPortsContextProvider>
                                                        </NodeExecutionResultContextProvider>
                                                    </NodeSelectedContextProvider>
                                                </NodeExecutionErrorContextProvider>
                                            </TriggerRunContextProvider>
                                        </PortPositionsContextProvider>
                                    </PortEventsContextProvider>
                                </NodeEventsContextProvider>
                            </NodeRunsContextProvider>
                        </NodeShapeContextProvider>
                    </NodeDimensionsContextProvider>
                </NodePositionContextProvider>
            </CurrentNodeInstanceContextProvider>
        </CurrentNodeContextProvider>
    );
};

export const NodeRenderer = React.memo(BaseNodeRenderer);
